/**
 * Les barres de défilement du design system, au web.
 *
 * Un `<ScrollView>` de react-native-web est un `div` qui défile : sans style, le navigateur y
 * dessine sa barre à lui — sous Windows, une barre grise de 17 px avec ses flèches, au milieu du
 * panneau de Sam. Ici, une barre fine (8 px), sans flèches ni rail, son curseur à la couleur du
 * contour du thème (`--colors-border`, que Unistyles pose sur la racine au web et repointe à chaque
 * thème, nuit comprise), plus marqué au survol (`--colors-text-muted`).
 *
 * **Seulement les défilements du design system** : ceux qui portent `data-ds-scroll`
 * (`dsScrollProps()`, étalé par les pièces qui défilent : `design-system/CLAUDE.md`, « Une zone qui
 * défile »). La feuille ne vise ni le document ni `*` : un défilement de l'ancien espace garde la barre du navigateur, interrupteur
 * éteint comme allumé. Le sélecteur passe par `:where()` (aucune précision) : un défilement que
 * l'écran masque (`showsVerticalScrollIndicator={false}`, une classe de react-native-web) le reste.
 *
 * Chromium et Safari lisent les pseudo-éléments `::-webkit-scrollbar` ; Firefox, qui ne les connaît
 * pas, lit `scrollbar-width` / `scrollbar-color`. Ces deux-là ne sont posées QUE pour lui : posées
 * pour Chromium (121+), elles lui feraient ignorer les pseudo-éléments, et il remettrait ses flèches.
 */
import { Platform } from "react-native"

/** L'attribut qui fait d'un défilement un défilement du design system, au web. */
export const DS_SCROLL_ATTRIBUTE = "data-ds-scroll"

type DataSet = Record<string, string>

/**
 * Ce qu'une pièce qui défile étale : l'attribut au web, rien sur le natif. Le `dataSet` de l'appelant
 * (`own`) s'y ajoute au web, sans retirer l'attribut.
 */
// `Platform?.` : sous Jest, un test qui simule `react-native` sans `Platform` rend un composant du DS.
export function dsScrollProps(own?: DataSet, os: string | undefined = Platform?.OS): { dataSet?: DataSet } {
    return os === "web" ? { dataSet: { ...own, dsScroll: "true" } } : {}
}

/** La largeur de la barre, en px : ce qui se pose au bord d'un défilement (un dégradé) s'arrête avant elle. */
export const DS_SCROLLBAR_WIDTH = 8

const DS = `:where([${DS_SCROLL_ATTRIBUTE}])`

export const WEB_SCROLLBAR_CSS = `
${DS}::-webkit-scrollbar { width: ${DS_SCROLLBAR_WIDTH}px; height: ${DS_SCROLLBAR_WIDTH}px; }
${DS}::-webkit-scrollbar-track, ${DS}::-webkit-scrollbar-corner { background: transparent; }
${DS}::-webkit-scrollbar-button { display: none; }
${DS}::-webkit-scrollbar-thumb {
  background-color: var(--colors-border);
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: content-box;
}
${DS}::-webkit-scrollbar-thumb:hover { background-color: var(--colors-text-muted); }
@supports not selector(::-webkit-scrollbar) {
  ${DS} { scrollbar-width: thin; scrollbar-color: var(--colors-border) transparent; }
}
`

const STYLE_TAG_ID = "masteur-scrollbars"

/** Pose la feuille dans le document, une fois. Sans document (le natif), ne fait rien. */
export function installWebScrollbars(
    doc: Document | undefined = typeof document === "undefined" ? undefined : document
) {
    if (doc == null || doc.getElementById(STYLE_TAG_ID) != null) return
    const style = doc.createElement("style")
    style.id = STYLE_TAG_ID
    style.textContent = WEB_SCROLLBAR_CSS
    doc.head.appendChild(style)
}
